/*
 * Number formatting shared by the buyer-side dashboard panels.
 *
 * Rupees use en-IN grouping (₹1,23,456), spot points are whole numbers,
 * percentages carry one decimal, and anything missing renders as "—" —
 * never "NaN", never "null".
 */

export const isNum = (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v));

export const num = (v, decimals = 0) =>
  isNum(v)
    ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : "—";

export const rupees = (v, decimals = 0) => (isNum(v) ? `₹${num(v, decimals)}` : "—");

export const pts = (v) => (isNum(v) ? `${num(v, 0)} pts` : "—");

export const pct = (v, decimals = 1) => (isNum(v) ? `${num(v, decimals)}%` : "—");

export const signedPts = (v) => (isNum(v) ? `${Number(v) > 0 ? "+" : ""}${num(v, 0)}` : "—");

/* "HH:MM" -> minutes since midnight, or null when unparsable. */
export const hhmmToMinutes = (s) => {
  if (typeof s !== "string") return null;
  const [h, m] = s.split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/* Seconds -> "12 s" / "3 min" / "2 h". */
export const age = (seconds) => {
  if (!isNum(seconds)) return "—";
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 90) return `${s} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} h`;
};

/* ISO-ish timestamp -> "HH:MM:SS" in IST; falls back to the raw tail. */
export const clockOf = (ts) => {
  if (!ts) return "—";
  // Backend timestamps are IST wall-clock and usually carry no zone. Without
  // one, `new Date` reads them in the BROWSER's zone, so a trader on a
  // non-IST machine saw every clock shifted. Pin naive stamps to +05:30.
  let iso = String(ts).replace(" ", "T");
  if (iso.includes("T") && !/(Z|[+-]\d{2}:?\d{2})$/i.test(iso)) iso += "+05:30";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(ts).slice(11, 19) || "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).format(d);
};

/* "2026-09-29" -> "Tue 29 Sep". */
export const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  // Built from en-US parts: en-GB/en-IN abbreviate September as "Sept".
  const parts = new Intl.DateTimeFormat("en-US", { weekday: "short", day: "numeric", month: "short" }).formatToParts(d);
  const get = (t) => parts.find((x) => x.type === t)?.value || "";
  return `${get("weekday")} ${get("day")} ${get("month")}`;
};

export const words = (s) => (s == null ? "" : String(s).replaceAll("_", " "));
